import classNames from "classnames";
import React, {
    ReactElement, ReactNode, useCallback, useEffect, useState,
} from "react";
import { Accept, useDropzone } from "react-dropzone";
import { AiOutlineCloseCircle, AiOutlineEdit } from "react-icons/ai";
import { RiUploadCloud2Line } from "@remixicon/react";
import { useCreateUploadTokenMutation } from "../../../__generated__/api";
import { useTheme } from "../../../contexts/Theme";
import { LoadingIcon } from "../../loading/Loading";
import Styles from "./IconUploader.module.scss";
import Icon from "../../theme/icon/Icon";

export const ACCEPT_IMAGES: Accept = {
    "image/png": [".png"],
    "image/jpeg": [".jpeg", ".jpg"],
    "image/gif": [".gif"],
};

export const ACCEPT_VIDEO: Accept = {
    "video/mp4": ["mp4", "m4v"],
    "video/webm": ["webm"],
    "video/ogg": ["ogg", "ogv"],
    "video/quicktime": ["mov"],
    "video/x-msvideo": ["avi"],
    "video/x-matroska": ["mkv"],
    "video/x-flv": ["flv"],
    "video/3gpp": ["3gp"],
    "video/mp2t": ["ts"],
    "video/x-m4v": ["m4v"],
};
export interface AttachmentUploaderProps {
    onChange: (url: string | undefined, mimeType: string | undefined, size: number | undefined, filename: string) => void;
    shouldStore?: boolean;
    accept?: Accept;
    dropClassName?: string;
    placeholder?: ReactNode
    className?: string;
    shouldOpen?: boolean;
}

export default function AttachmentUploader({
    onChange, shouldStore, accept, dropClassName, placeholder, className, shouldOpen,
}: AttachmentUploaderProps): ReactElement {
    const [uploadAttachment] = useCreateUploadTokenMutation();

    const [isUploading, setIsUploading] = useState(false);

    const onDrop = useCallback(async (acceptedFile: File[]) : Promise<undefined> => {
        setIsUploading(true);
        const file = acceptedFile[0];

        if (!file) {
            setIsUploading(false);
            return;
        }
        try {
            const meta = await uploadAttachment({
                variables: {
                    attachment: {
                        filename: file.name,
                        size: file.size,
                        mimeType: file.type,
                        shouldStore,
                    },
                },
            });

            if (!meta.data?.createUploadToken || meta.data.createUploadToken?.error) {
                return;
            }
            const { url } = meta.data.createUploadToken;

            const response = await fetch(url, {
                method: "POST",
                body: file,
                credentials: "include",
            });

            if (response.status !== 201) {
                return;
            }

            const json = await response.json() as {url : string};
            onChange(json.url, file.type, file.size, file.name);
        } finally {
            setIsUploading(false);
        }
    }, []);

    const {
        getRootProps, getInputProps, isDragActive, open,
    } = useDropzone({
        onDrop,
        multiple: false,
        maxFiles: 1,
        maxSize: 2e+7,
        accept,
    });

    useEffect(() => {
        if (shouldOpen) {
            open();
        }
    }, []);

    let control: ReactElement;
    if (isUploading) {
        control = (
            <div className={classNames(Styles["attachment-drop"], dropClassName)}>
                <LoadingIcon />
            </div>
        );
    } else if (isDragActive) {
        control = (
            <div
                className={classNames(Styles["attachment-drop"], dropClassName)}
            >
                Drag and Drop files here
            </div>
        );
    } else {
        control = (
            <>
                <div className={classNames(Styles["default-drop"], dropClassName)}>
                    {placeholder ?? (
                        <div>
                            <Icon icon={<RiUploadCloud2Line />} />
                            <div>
                                Click or drag to upload
                            </div>
                        </div>
                    )}
                </div>

                <input {...getInputProps()} />
            </>
        );
    }

    return (

        <div className={className} {...getRootProps()}>
            {control}
        </div>

    );
}

AttachmentUploader.defaultProps = {
    shouldStore: false,
    accept: undefined,
    dropClassName: undefined,
    placeholder: undefined,
    className: undefined,
    shouldOpen: false,
};
